/**
 * CareLogix™ ORACLE — LifeScore™ Daily Intelligence (additive).
 *
 * Truthfulness Mandate:
 *  - No fabricated readings. If no inputs are supplied, the page shows the
 *    truthful NO_DATA_AVAILABLE state.
 *  - All confidence + missing-pillar information is surfaced verbatim from
 *    the backend response.
 *  - Decision-support only. No diagnosis. No treatment.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

const BACKEND =
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) ||
  "";

const PILLAR_LABELS = {
  nutrition:  "Nutrition",
  hydration:  "Hydration",
  sleep:      "Sleep",
  activity:   "Activity",
  medication: "Medication adherence",
  recovery:   "Recovery",
};

const BAND_COLOR = {
  "Elite":           "#7AC860",
  "Excellent":       "#7AC860",
  "Good":            "#c5a059",
  "Moderate":        "#fcd34d",
  "Needs Attention": "#ef4444",
};

async function _get(p) {
  try { const r = await fetch(`${BACKEND}${p}`); if (!r.ok) return null; return await r.json(); }
  catch { return null; }
}
async function _post(p, body) {
  try {
    const r = await fetch(`${BACKEND}${p}`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return await r.json();
  } catch (e) { return { ok: false, error: String(e) }; }
}

const S = {
  page: { minHeight: "100vh", padding: "32px 40px 80px", background: "linear-gradient(160deg, #050b09 0%, #06231b 60%, #050b09 100%)", color: "#dfe3eb", fontFamily: "'Figtree', 'Inter', system-ui, sans-serif" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, flexWrap: "wrap", marginBottom: 22 },
  eyebrow: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.18em", marginBottom: 4 },
  title: { color: "#fde68a", margin: 0, fontSize: 28, fontFamily: "'Cinzel', serif", letterSpacing: "0.02em" },
  sub: { color: "#cbd5e1", marginTop: 8, fontSize: 13, maxWidth: 760, lineHeight: 1.5 },
  link: { color: "#c5a059", textDecoration: "none", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.1em", marginRight: 18 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginTop: 14, marginBottom: 24 },
  kpi: { background: "rgba(13,26,15,0.92)", border: "1px solid rgba(245,200,66,0.18)", borderRadius: 12, padding: "14px 16px" },
  kpiLabel: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.14em" },
  kpiValue: { color: "#fde68a", fontFamily: "'Cinzel', serif", fontSize: 30, marginTop: 6 },
  kpiSub: { color: "#94a3b8", fontSize: 11, marginTop: 4 },
  section: { background: "rgba(13,26,15,0.65)", border: "1px solid rgba(245,200,66,0.20)", borderRadius: 14, padding: 18, marginBottom: 20 },
  sectionTitle: { color: "#fde68a", fontFamily: "'Cinzel', serif", fontSize: 18, margin: 0, marginBottom: 4 },
  sectionSub: { color: "#cbd5e1", fontSize: 12, lineHeight: 1.5, marginBottom: 12, marginTop: 4 },
  inputRow: { display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 },
  inputBox: { display: "flex", flexDirection: "column", minWidth: 150, flex: 1 },
  inputLabel: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.10em", marginBottom: 4 },
  input: { background: "#040608", border: "1px solid rgba(255,255,255,0.10)", color: "#e2e8f0", padding: "9px 11px", borderRadius: 10, fontSize: 12.5 },
  btn: { appearance: "none", background: "linear-gradient(135deg, #c5a059, #ffd966)", border: "1px solid #c5a059", color: "#0c1a10", padding: "9px 14px", borderRadius: 10, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800, letterSpacing: "0.06em" },
  btnSecondary: { appearance: "none", background: "transparent", border: "1px solid rgba(255,255,255,0.20)", color: "#cbd5e1", padding: "8px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11 },
  pill: { fontFamily: "'Space Mono', monospace", fontSize: 10, fontWeight: 800, letterSpacing: "0.10em", padding: "3px 10px", border: "1px solid", borderRadius: 999 },
  compliance: { marginTop: 28, padding: "14px 18px", background: "rgba(252,211,77,0.06)", border: "1px solid rgba(252,211,77,0.30)", borderRadius: 12, color: "#fde68a", fontSize: 12, lineHeight: 1.5 },
};

function Pill({ children, color }) {
  return <span style={{ ...S.pill, color, borderColor: color, background: `${color}1a` }}>{children}</span>;
}

export default function OracleLifeScorePage() {
  const [manifest, setManifest] = useState(null);
  const [today, setToday]       = useState(null);
  const [userId, setUserId]     = useState("");
  const [form, setForm]         = useState({});
  const [busy, setBusy]         = useState("");
  const [computed, setComputed] = useState(null);
  const [history, setHistory]   = useState([]);
  const _once = useRef(false);

  useEffect(() => {
    if (_once.current) return; _once.current = true;
    (async () => {
      const [m, t] = await Promise.all([
        _get("/api/oracle/lifescore/manifest"),
        _get("/api/oracle/lifescore/today?tier=executive"),
      ]);
      setManifest(m); setToday(t);
    })();
    try {
      const saved = window.localStorage.getItem("carelogix_lifescore_user");
      if (saved) setUserId(saved);
    } catch (_e) { /* ignore */ }
  }, []);

  const loadHistory = useCallback(async (uid) => {
    if (!uid) return;
    const h = await _get(`/api/oracle/lifescore/history?user_id=${encodeURIComponent(uid)}&limit=14`);
    setHistory((h && h.history) || []);
  }, []);

  const onSelectUser = useCallback(() => {
    if (!userId || userId.length < 2) return;
    try { window.localStorage.setItem("carelogix_lifescore_user", userId); } catch (_e) { /* ignore */ }
    loadHistory(userId);
  }, [userId, loadHistory]);

  const calculate = useCallback(async () => {
    setBusy("calc");
    const inputs = {};
    Object.entries(form).forEach(([k, v]) => {
      const n = v === "" || v === null || v === undefined ? null : Number(v);
      if (n != null && !Number.isNaN(n)) inputs[k] = Math.max(0, Math.min(1, n / 100));
    });
    const out = await _post("/api/oracle/lifescore/calculate", { inputs, user_id: userId || null });
    setBusy("");
    setComputed(out && out.lifescore ? out.lifescore : null);
    if (userId) loadHistory(userId);
  }, [form, userId, loadHistory]);

  const todayScore   = computed || (today && today.lifescore) || null;
  const todayBand    = todayScore && todayScore.tier;
  const todayColor   = BAND_COLOR[todayBand] || "#94a3b8";
  const wearableStatus = (today && today.wearable_status) || "Coming Soon";

  const pillarKeys = useMemo(() => Object.keys(PILLAR_LABELS), []);

  return (
    <main style={S.page} data-testid="oracle-lifescore-page">
      <header style={S.header}>
        <div>
          <div style={S.eyebrow}>ORACLE LIFECOMMAND™ · LIFESCORE™</div>
          <h1 style={S.title}>Daily Habit Intelligence — LifeScore™</h1>
          <p style={S.sub}>
            ORACLE unifies <strong>nutrition · hydration · sleep · activity · medication · recovery</strong> into a single 0-100 LifeScore™.
            Each pillar contributes proportionally to its declared weight. Missing pillars stay missing — ORACLE never fabricates a reading.
            <strong> Decision-support only. No diagnosis. No treatment.</strong>
          </p>
        </div>
        <div>
          <Link to="/oracle/wearables" style={S.link} data-testid="link-wearables">Wearables →</Link>
          <Link to="/oracle/animations" style={S.link} data-testid="link-animations">Animation Library →</Link>
          <Link to="/oracle/lifecommand" style={S.link} data-testid="link-lifecommand">LifeCommand Console →</Link>
        </div>
      </header>

      <section data-testid="lifescore-kpis">
        <div style={S.grid}>
          <div style={S.kpi} data-testid="kpi-today">
            <div style={S.kpiLabel}>TODAY&apos;S LIFESCORE</div>
            <div style={S.kpiValue}>{todayScore && todayScore.score != null ? todayScore.score : "—"}</div>
            <div style={S.kpiSub}>{todayBand ? <Pill color={todayColor}>{todayBand}</Pill> : <span style={{ color: "#fcd34d" }}>NO_DATA_AVAILABLE</span>}</div>
          </div>
          <div style={S.kpi} data-testid="kpi-confidence">
            <div style={S.kpiLabel}>CONFIDENCE</div>
            <div style={S.kpiValue}>{todayScore && todayScore.confidence != null ? `${Math.round(todayScore.confidence * 100)}%` : "—"}</div>
            <div style={S.kpiSub}>Based on contributed pillars</div>
          </div>
          <div style={S.kpi} data-testid="kpi-sources">
            <div style={S.kpiLabel}>SOURCES USED</div>
            <div style={S.kpiValue}>{todayScore && Array.isArray(todayScore.sources_used) ? todayScore.sources_used.length : 0}</div>
            <div style={S.kpiSub}>{todayScore && (todayScore.sources_used || []).join(", ") || "—"}</div>
          </div>
          <div style={S.kpi} data-testid="kpi-wearables">
            <div style={S.kpiLabel}>WEARABLE INGEST</div>
            <div style={{ ...S.kpiValue, fontSize: 16, marginTop: 12 }}>
              <Pill color={wearableStatus === "LIVE" ? "#7AC860" : "#fcd34d"}>{wearableStatus === "LIVE" ? "Live" : "Coming Soon"}</Pill>
            </div>
            <div style={S.kpiSub}>No fabricated readings</div>
          </div>
        </div>
      </section>

      {/* User context */}
      <section style={S.section} data-testid="lifescore-user">
        <h2 style={S.sectionTitle}>Your LifeScore™ session</h2>
        <p style={S.sectionSub}>Optional member identifier — used to persist your history. No PHI required.</p>
        <div style={S.inputRow}>
          <input data-testid="lifescore-user-input" style={{ ...S.input, flex: 1, minWidth: 240 }} placeholder="Your member email or ID" value={userId} onChange={(e) => setUserId(e.target.value)} />
          <button data-testid="lifescore-user-load" type="button" style={S.btn} onClick={onSelectUser}>Load history</button>
        </div>
      </section>

      {/* Pillar inputs */}
      <section style={S.section} data-testid="lifescore-inputs">
        <h2 style={S.sectionTitle}>Today&apos;s pillar inputs</h2>
        <p style={S.sectionSub}>
          Enter a 0-100 score for each pillar you can self-report. Leave a pillar blank to honestly report <strong>NO_DATA</strong> for that pillar.
        </p>
        <div style={S.inputRow}>
          {pillarKeys.map((k) => (
            <div key={k} style={S.inputBox}>
              <span style={S.inputLabel}>{PILLAR_LABELS[k]} (0-100)</span>
              <input
                type="number" min="0" max="100"
                style={S.input}
                value={form[k] != null ? form[k] : ""}
                onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                data-testid={`lifescore-input-${k}`}
              />
            </div>
          ))}
        </div>
        <div style={{ ...S.inputRow, marginTop: 14 }}>
          <button type="button" style={S.btn} disabled={busy === "calc"} onClick={calculate} data-testid="lifescore-calc-btn">
            {busy === "calc" ? "Calculating…" : "Calculate LifeScore™"}
          </button>
          <button type="button" style={S.btnSecondary} onClick={() => { setForm({}); setComputed(null); }} data-testid="lifescore-clear-btn">Clear</button>
        </div>

        {computed && (
          <div style={{ marginTop: 14 }} data-testid="lifescore-result">
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <Pill color={todayColor}>{computed.tier}</Pill>
              <span style={{ color: "#fde68a", fontFamily: "'Cinzel', serif", fontSize: 22 }}>{computed.score != null ? computed.score : "NO_DATA"}</span>
              <span style={{ color: "#94a3b8", fontSize: 12 }}>confidence {Math.round((computed.confidence || 0) * 100)}%</span>
            </div>
            {Array.isArray(computed.missing_data) && computed.missing_data.length > 0 && (
              <p style={{ color: "#fcd34d", fontSize: 12, marginTop: 8 }}>
                Missing pillars (honest NO_DATA): {computed.missing_data.map((k) => PILLAR_LABELS[k] || k).join(", ")}
              </p>
            )}
          </div>
        )}
      </section>

      {/* Pillar weights */}
      {manifest && manifest.weights && (
        <section style={S.section} data-testid="lifescore-weights">
          <h2 style={S.sectionTitle}>Pillar weights</h2>
          <p style={S.sectionSub}>Transparent scoring contract — total weights = 100.</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
            {Object.entries(manifest.weights).map(([k, w]) => (
              <div key={k} style={S.kpi} data-testid={`weight-${k}`}>
                <div style={S.kpiLabel}>{PILLAR_LABELS[k] || k}</div>
                <div style={{ ...S.kpiValue, fontSize: 22 }}>{w}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* History */}
      {history.length > 0 && (
        <section style={S.section} data-testid="lifescore-history">
          <h2 style={S.sectionTitle}>Recent LifeScore history</h2>
          <p style={S.sectionSub}>Showing last {history.length} entries.</p>
          <div style={{ display: "grid", gap: 8 }}>
            {history.map((h, i) => (
              <div key={i} style={{ ...S.kpi, padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }} data-testid={`history-row-${i}`}>
                <span style={{ color: "#cbd5e1", fontSize: 12 }}>{h.created_at}</span>
                <span style={{ color: "#fde68a", fontFamily: "'Cinzel', serif", fontSize: 18 }}>
                  {h.result && h.result.score != null ? h.result.score : "—"}
                </span>
                <Pill color={BAND_COLOR[h.result && h.result.tier] || "#94a3b8"}>{(h.result && h.result.tier) || "NO_DATA"}</Pill>
              </div>
            ))}
          </div>
        </section>
      )}

      <div style={S.compliance} data-testid="lifescore-compliance">
        {(manifest && manifest.disclaimer) || "Decision-support only. Not medical advice. Not a diagnosis. Human review required."}
      </div>
    </main>
  );
}
